import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { isUniEnzymeTestkitExists } from '@wix/wix-ui-test-utils/enzyme';
import { isUniTestkitExists } from '@wix/wix-ui-test-utils/vanilla';
import { mount } from 'enzyme';
import {
  reviewSummaryDriverFactory,
  ReviewSummaryDriver,
} from './ReviewSummary.driver';
import { reviewSummaryTestkitFactory } from '../../testkit';
import { reviewSummaryTestkitFactory as enzymeReviewSummaryTestkitFactory } from '../../testkit/enzyme';
import { ReviewSummary, ReviewSummaryProps } from '.';

describe('ReviewSummary', () => {
  const createDriver = createRendererWithUniDriver<ReviewSummaryDriver>(
    reviewSummaryDriverFactory,
  );

  const bootstrap = (props: ReviewSummaryProps = { items: [] }) => {
    return createDriver(<ReviewSummary {...props} />);
  };

  it('should render', async () => {
    const { driver } = bootstrap();
    expect(await driver.exists()).toBe(true);
  });

  it('should render items properly', async () => {
    const items = [
      {
        value: 35,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '35',
      },
      {
        value: 10,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '10',
      },
      {
        value: 8,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '8',
      },
    ];
    const { driver } = bootstrap({ items });

    expect(await driver.getNumberOfItems()).toBe(3);
    await Promise.all(
      items.map(async (item, index) => {
        const itemDriver = driver.getItemAt(index);
        expect(await itemDriver.exists()).toBe(true);
        expect(await itemDriver.getSuffixIndicationText()).toBe(
          item.customSuffixIndicationText,
        );
      }),
    );
  });

  it('should be clickable', async () => {
    const onClick = jest.fn();
    const items = [
      {
        value: 35,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '35',
        onClick,
      },
    ];
    const { driver } = bootstrap({ items });
    await driver.getItemAt(0).click();
    expect(onClick).toHaveBeenCalledTimes(1);
  });

  it('all items should be clickable if any item is clickable', async () => {
    const items = [
      {
        value: 35,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '35',
        onClick: jest.fn(),
      },
      {
        value: 10,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '10',
      },
      {
        value: 8,
        max: 53,
        prefixIndication: <div>stars</div>,
        showProgressIndication: true,
        customSuffixIndicationText: '8',
      },
    ];
    const { driver } = bootstrap({ items });

    const element = await driver.element();
    const buttons = await element.querySelectorAll('button');
    expect(buttons.length).toBe(items.length);
  });

  describe('testkit', () => {
    it('should exist', async () => {
      expect(
        await isUniTestkitExists(
          <ReviewSummary items={[]} />,
          reviewSummaryTestkitFactory,
          {
            dataHookPropName: 'data-hook',
          },
        ),
      ).toBe(true);
    });
  });

  describe('enzyme testkit', () => {
    it('should exist', async () => {
      expect(
        await isUniEnzymeTestkitExists(
          <ReviewSummary items={[]} />,
          enzymeReviewSummaryTestkitFactory,
          mount,
          {
            dataHookPropName: 'data-hook',
          },
        ),
      ).toBe(true);
    });
  });
});
